/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import axios from 'axios';
import {
    AlertTriangle,
    ArrowLeft,
    CheckCircle2,
    Eye,
    EyeOff,
    LockKeyhole,
    Mail,
    ShieldAlert,
} from 'lucide-react';

import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

const SUPPORT_EMAIL = 'codexpokhara@gmail.com';

const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`
    + '?subject=TIPA%20Account%20Deletion%20Help'
    + '&body=I%20need%20help%20deleting%20my%20TIPA%20account.'
    + '%0A%0AAccount%20email%2Fusername%3A%20'
    + '%0AWhat%20went%20wrong%3A%20';

const CONFIRM_PHRASE = 'DELETE MY ACCOUNT';

const API_BASE = import.meta.env.VITE_APP_SERVER_URL;

type Step = 'identity' | 'review' | 'confirm' | 'done';

interface SubscriptionInfo {
    tier?: string;
    status?: string;
    paid_until?: string | null;
    forfeited?: boolean;
}

interface WillDelete {
    team_members?: number;
    team_member_usernames?: string[];
    products?: number;
    groups?: number;
    categories?: number;
    units?: number;
    storage_places?: number;
    announcements?: number;
    orders_placed?: number;
    orders_received?: number;
    connections?: number;
    reviews_written?: number;
    reviews_received?: number;
    subscription?: SubscriptionInfo | null;
    retained_note?: string;
}

interface DeleteResult {
    deleted_accounts?: number;
    subscription_forfeited?: boolean;
}

const COUNT_LABELS: { key: keyof WillDelete; label: string }[] = [
    { key: 'products', label: 'Products' },
    { key: 'groups', label: 'Groups' },
    { key: 'categories', label: 'Categories' },
    { key: 'units', label: 'Units' },
    { key: 'storage_places', label: 'Storage places' },
    { key: 'announcements', label: 'Announcements' },
    { key: 'orders_placed', label: 'Orders placed' },
    { key: 'orders_received', label: 'Orders received' },
    { key: 'connections', label: 'Connections' },
    { key: 'reviews_written', label: 'Reviews written' },
    { key: 'reviews_received', label: 'Reviews received' },
    { key: 'team_members', label: 'Team member accounts' },
];

function readApiError(data: any, fallback: string): string {
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.password) && data.password.length > 0) {
        return String(data.password[0]);
    }
    if (Array.isArray(data.email) && data.email.length > 0) {
        return String(data.email[0]);
    }
    if (typeof data.message === 'string') return data.message;
    return fallback;
}

function formatDate(value?: string | null): string | null {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

function Card({ className, children }: { className?: string; children: React.ReactNode }) {
    return (
        <div className={cn('rounded-2xl border border-border bg-card p-6', className)}>
            {children}
        </div>
    );
}

function StepIndicator({ current }: { current: Step }) {
    const steps: { id: Step; label: string }[] = [
        { id: 'identity', label: 'Verify identity' },
        { id: 'review', label: 'Review data' },
        { id: 'confirm', label: 'Confirm' },
        { id: 'done', label: 'Deleted' },
    ];
    const currentIndex = steps.findIndex((step) => step.id === current);

    return (
        <ol className="mb-8 flex flex-wrap items-center gap-x-2 gap-y-2 text-xs font-medium">
            {steps.map((step, index) => {
                const isActive = index === currentIndex;
                const isDone = index < currentIndex;
                return (
                    <li key={step.id} className="flex items-center gap-2">
                        <span
                            className={cn(
                                'flex h-6 w-6 items-center justify-center rounded-full border text-[0.7rem]',
                                isActive && 'border-primary bg-primary text-primary-foreground',
                                isDone && 'border-primary/40 bg-primary/10 text-primary',
                                !isActive && !isDone && 'border-border text-muted-foreground',
                            )}
                        >
                            {index + 1}
                        </span>
                        <span
                            className={cn(
                                isActive ? 'text-foreground' : 'text-muted-foreground',
                            )}
                        >
                            {step.label}
                        </span>
                        {index < steps.length - 1 && (
                            <span className="mx-1 hidden h-px w-6 bg-border sm:block" />
                        )}
                    </li>
                );
            })}
        </ol>
    );
}

function ErrorBanner({ message }: { message: string }) {
    return (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{message}</span>
        </div>
    );
}

function DeleteAccount() {
    const [step, setStep] = useState<Step>('identity');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [willDelete, setWillDelete] = useState<WillDelete | null>(null);
    const [result, setResult] = useState<DeleteResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = 'Delete Your Account | Tipa';
        return () => {
            document.title = previousTitle;
        };
    }, []);

    const canSubmitIdentity = email.trim().length > 0 && password.length > 0 && !loading;
    const canConfirm = confirmText.trim().toUpperCase() === CONFIRM_PHRASE && !loading;

    const nonZeroCounts = useMemo(() => {
        if (!willDelete) return [];
        return COUNT_LABELS
            .map((entry) => ({
                label: entry.label,
                value: Number(willDelete[entry.key] ?? 0),
            }))
            .filter((entry) => entry.value > 0);
    }, [willDelete]);

    // Step 1: authenticate, then call the preview (no `confirm` flag) so the user
    // sees exactly what will be destroyed before anything is touched.
    const handleIdentitySubmit = useCallback(async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const loginResponse = await axios.post(
                `${API_BASE}/system/auth/login/`,
                { email: email.trim(), password },
                { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } },
            );

            const token = loginResponse.data?.tokens?.access;
            if (!token) {
                setError('Could not verify your account. Please try again.');
                return;
            }

            const previewResponse = await axios.post(
                `${API_BASE}/system/auth/delete-account/`,
                { password },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            setAccessToken(token);
            setWillDelete(previewResponse.data?.will_delete ?? {});
            setStep('review');
        } catch (caught: any) {
            const status = caught?.response?.status;
            const data = caught?.response?.data;

            if (!caught?.response) {
                setError('Network error. Check your connection and try again.');
            } else if (status === 401 || status === 400) {
                setError(readApiError(data, 'Email or password is incorrect.'));
            } else if (status === 403) {
                setError(readApiError(
                    data,
                    'Only the account owner can delete this account. Ask your owner to remove you from the team instead.',
                ));
            } else {
                setError(readApiError(data, 'Something went wrong. Please try again.'));
            }
        } finally {
            setLoading(false);
        }
    }, [email, password]);

    // Step 3: the irreversible call.
    const handleFinalDelete = useCallback(async () => {
        if (!accessToken) return;
        setError(null);
        setLoading(true);

        try {
            const response = await axios.post(
                `${API_BASE}/system/auth/delete-account/`,
                { password, confirm: true },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization: `Bearer ${accessToken}`,
                    },
                },
            );

            setResult({
                deleted_accounts: response.data?.deleted_accounts,
                subscription_forfeited: response.data?.subscription_forfeited,
            });
            setAccessToken(null);
            setPassword('');
            setStep('done');
        } catch (caught: any) {
            setError(readApiError(
                caught?.response?.data,
                'Your account could not be deleted. Please try again.',
            ));
        } finally {
            setLoading(false);
        }
    }, [accessToken, password]);

    const handleStartOver = useCallback(() => {
        setStep('identity');
        setPassword('');
        setConfirmText('');
        setAccessToken(null);
        setWillDelete(null);
        setError(null);
    }, []);

    const subscription = willDelete?.subscription;
    const paidUntil = formatDate(subscription?.paid_until);

    return (
        <div className="min-h-[100dvh] bg-background text-foreground">
            <header className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur-xl">
                <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5 md:px-8">
                    <span className="text-[1.05rem] font-bold tracking-tight text-foreground">
                        Tipa
                    </span>
                    <ThemeToggle />
                </div>
            </header>

            <main className="mx-auto max-w-3xl px-5 pb-24 pt-10 md:px-8 md:pt-14">
                <div className="mb-10">
                    <span className="inline-block rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold tracking-wide text-primary">
                        TIPA
                    </span>
                    <h1 className="mt-4 text-3xl font-bold tracking-tight text-foreground md:text-4xl">
                        Delete Your Account
                    </h1>
                    <p className="mt-3 text-[0.95rem] text-muted-foreground">
                        Permanently delete your TIPA account and its data. You complete the
                        whole process here — no email request and no support call needed.
                    </p>
                </div>

                <StepIndicator current={step} />

                {step === 'identity' && (
                    <div className="space-y-8">
                        <div className="flex items-start gap-3 rounded-2xl border border-destructive/40 bg-destructive/10 p-5">
                            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                            <div className="text-sm leading-relaxed text-destructive">
                                <p className="font-semibold">
                                    Deletion is permanent and cannot be reversed.
                                </p>
                                <p className="mt-1">
                                    Once your account is deleted we cannot restore it or recover
                                    any of your data — not by request, not by support, not later.
                                    There is no undo and no grace period.
                                </p>
                            </div>
                        </div>

                        <section>
                            <h2 className="text-xl font-semibold tracking-tight text-foreground">
                                Step 1 — Verify it&apos;s you
                            </h2>
                            <p className="mt-2 text-[0.95rem] text-muted-foreground">
                                Enter the email and password of the TIPA account you want
                                deleted. We verify your identity before showing you anything.
                            </p>

                            <Card className="mt-4">
                                <form onSubmit={handleIdentitySubmit} className="grid gap-5">
                                    <div className="grid gap-2">
                                        <Label htmlFor="delete-email">Account email</Label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                            <Input
                                                id="delete-email"
                                                type="email"
                                                autoComplete="username"
                                                placeholder="you@example.com"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                className="pl-10"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid gap-2">
                                        <Label htmlFor="delete-password">Password</Label>
                                        <div className="relative">
                                            <LockKeyhole className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                            <Input
                                                id="delete-password"
                                                type={passwordVisible ? 'text' : 'password'}
                                                autoComplete="current-password"
                                                placeholder="Enter your password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="pl-10 pr-10"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setPasswordVisible((prev) => !prev)}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                                            >
                                                {passwordVisible
                                                    ? <EyeOff className="h-4 w-4" />
                                                    : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>

                                    <Button type="submit" disabled={!canSubmitIdentity} className="w-full">
                                        {loading && <Spinner className="mr-2 h-4 w-4" />}
                                        Continue
                                    </Button>

                                    {error && <ErrorBanner message={error} />}
                                </form>
                            </Card>

                            <p className="mt-3 text-sm text-muted-foreground">
                                Signed up with Google and never set a password? Reset your
                                password in the TIPA app first, then come back here.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold tracking-tight text-foreground">
                                Who can delete an account
                            </h2>
                            <p className="mt-3 text-[0.95rem] leading-relaxed text-muted-foreground">
                                Only the business owner can delete a TIPA account. If you are a
                                team member, your login cannot be deleted here — ask your account
                                owner to remove you from the team. Deleting the owner account also
                                deletes every team member account under that business.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold tracking-tight text-foreground">
                                What gets deleted
                            </h2>
                            <ul className="mt-4 list-disc space-y-1.5 pl-5 text-[0.95rem] leading-relaxed text-muted-foreground">
                                <li>
                                    Your account profile — email, username, business name, phone
                                    number, address, and profile photo
                                </li>
                                <li>
                                    Your inventory data — products, stock levels, categories,
                                    groups, units, and storage places
                                </li>
                                <li>Stock and activity logs associated with your account</li>
                                <li>Announcements, connections, and your review history</li>
                                <li>
                                    Any team member accounts you created under your business, and
                                    their associated data
                                </li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold tracking-tight text-foreground">
                                What is retained
                            </h2>
                            <p className="mt-4 text-[0.95rem] leading-relaxed text-muted-foreground">
                                Orders and reviews you exchanged with other businesses stay in
                                their records, because the other business is entitled to its own
                                trade history. Your name and contact details are stripped from
                                those records. We may also keep minimal records for a limited
                                period where required by law or to prevent fraud and abuse, after
                                which they are removed too.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-xl font-semibold tracking-tight text-foreground">
                                Deleting from inside the app
                            </h2>
                            <p className="mt-4 text-[0.95rem] leading-relaxed text-muted-foreground">
                                You can also delete your account without this page: open the TIPA
                                app and go to Profile → Privacy Settings → Delete Account, then
                                enter your password and confirm.
                            </p>
                        </section>
                    </div>
                )}

                {step === 'review' && (
                    <div className="space-y-8">
                        <section>
                            <h2 className="text-xl font-semibold tracking-tight text-foreground">
                                Step 2 — Review what will be deleted
                            </h2>
                            <p className="mt-2 text-[0.95rem] text-muted-foreground">
                                Nothing has been deleted yet. This is the actual content on your
                                account right now.
                            </p>

                            <Card className="mt-4">
                                {nonZeroCounts.length > 0 ? (
                                    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                                        {nonZeroCounts.map((entry) => (
                                            <div key={entry.label}>
                                                <dt className="text-xs text-muted-foreground">
                                                    {entry.label}
                                                </dt>
                                                <dd className="text-lg font-semibold text-foreground">
                                                    {entry.value}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                ) : (
                                    <p className="text-[0.95rem] text-muted-foreground">
                                        Your account has no stored business records. Your profile
                                        and login will still be permanently deleted.
                                    </p>
                                )}
                            </Card>

                            {willDelete?.team_member_usernames
                                && willDelete.team_member_usernames.length > 0 && (
                                <Card className="mt-4 border-destructive/40 bg-destructive/5">
                                    <p className="text-sm font-semibold text-foreground">
                                        These team member accounts will also be deleted
                                    </p>
                                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                                        {willDelete.team_member_usernames.map((username) => (
                                            <li key={username}>{username}</li>
                                        ))}
                                    </ul>
                                </Card>
                            )}

                            {subscription && (
                                <Card className="mt-4 border-destructive/40 bg-destructive/5">
                                    <p className="text-sm font-semibold text-foreground">
                                        Your subscription will be forfeited
                                    </p>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        {subscription.tier ?? 'Your'}
                                        {' '}
                                        plan
                                        {paidUntil ? ` is paid until ${paidUntil}` : ' is active'}
                                        . Remaining paid time is forfeited on deletion and is
                                        <strong> not refunded</strong>
                                        .
                                    </p>
                                </Card>
                            )}

                            {willDelete?.retained_note && (
                                <Card className="mt-4">
                                    <p className="text-sm font-semibold text-foreground">
                                        What we keep
                                    </p>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        {willDelete.retained_note}
                                    </p>
                                </Card>
                            )}

                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <Button
                                    variant="outline"
                                    onClick={handleStartOver}
                                    className="sm:w-auto"
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Cancel
                                </Button>
                                <Button
                                    variant="destructive"
                                    onClick={() => { setError(null); setStep('confirm'); }}
                                    className="sm:w-auto"
                                >
                                    Continue to final confirmation
                                </Button>
                            </div>

                            {error && <ErrorBanner message={error} />}
                        </section>
                    </div>
                )}

                {step === 'confirm' && (
                    <div className="space-y-8">
                        <div className="flex items-start gap-3 rounded-2xl border border-destructive/40 bg-destructive/10 p-5">
                            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                            <div className="text-sm leading-relaxed text-destructive">
                                <p className="font-semibold">This is the point of no return.</p>
                                <p className="mt-1">
                                    Pressing the button below deletes your account and all the
                                    data listed on the previous step immediately. Deleted data
                                    cannot be recovered or restored by anyone, including us.
                                </p>
                            </div>
                        </div>

                        <section>
                            <h2 className="text-xl font-semibold tracking-tight text-foreground">
                                Step 3 — Confirm deletion
                            </h2>
                            <p className="mt-2 text-[0.95rem] text-muted-foreground">
                                Type
                                {' '}
                                <code className="rounded border border-border bg-muted px-1.5 py-0.5 text-[0.85em]">
                                    {CONFIRM_PHRASE}
                                </code>
                                {' '}
                                to enable the delete button.
                            </p>

                            <Card className="mt-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="delete-confirm">Confirmation</Label>
                                    <Input
                                        id="delete-confirm"
                                        type="text"
                                        autoComplete="off"
                                        placeholder={CONFIRM_PHRASE}
                                        value={confirmText}
                                        onChange={(e) => setConfirmText(e.target.value)}
                                    />
                                </div>

                                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                                    <Button
                                        variant="outline"
                                        onClick={() => { setError(null); setStep('review'); }}
                                        className="sm:w-auto"
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Back
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        onClick={handleFinalDelete}
                                        disabled={!canConfirm}
                                        className="sm:w-auto"
                                    >
                                        {loading && <Spinner className="mr-2 h-4 w-4" />}
                                        Permanently delete my account
                                    </Button>
                                </div>

                                {error && <ErrorBanner message={error} />}
                            </Card>
                        </section>
                    </div>
                )}

                {step === 'done' && (
                    <div className="space-y-8">
                        <Card className="border-primary/40 bg-primary/5">
                            <div className="flex items-start gap-3">
                                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                                <div>
                                    <h2 className="text-xl font-semibold tracking-tight text-foreground">
                                        Your account has been deleted
                                    </h2>
                                    <p className="mt-2 text-[0.95rem] leading-relaxed text-muted-foreground">
                                        Your TIPA account and its data have been permanently
                                        removed. You can no longer log in with this account, and
                                        the data cannot be recovered.
                                    </p>
                                    {typeof result?.deleted_accounts === 'number'
                                        && result.deleted_accounts > 1 && (
                                        <p className="mt-2 text-[0.95rem] text-muted-foreground">
                                            {result.deleted_accounts}
                                            {' '}
                                            accounts were deleted, including your team members.
                                        </p>
                                    )}
                                    {result?.subscription_forfeited && (
                                        <p className="mt-2 text-[0.95rem] text-muted-foreground">
                                            Your remaining subscription time was forfeited and is
                                            not refundable.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </Card>

                        <section>
                            <h2 className="text-xl font-semibold tracking-tight text-foreground">
                                What we still hold
                            </h2>
                            <p className="mt-3 text-[0.95rem] leading-relaxed text-muted-foreground">
                                Orders and reviews you exchanged with other businesses remain in
                                their records with your name and contact details removed, and we
                                may keep minimal records for a limited period where the law
                                requires it. Everything else is gone.
                            </p>
                        </section>

                        <p className="text-[0.95rem] text-muted-foreground">
                            Remember to uninstall the TIPA app from your device if you no longer
                            need it.
                        </p>
                    </div>
                )}

                <section className="mt-12">
                    <h2 className="text-xl font-semibold tracking-tight text-foreground">
                        Need help?
                    </h2>
                    <p className="mt-3 text-[0.95rem] leading-relaxed text-muted-foreground">
                        Deletion does not require contacting us — the steps above complete it.
                        If something on this page fails, write to
                        {' '}
                        <a
                            href={SUPPORT_MAILTO}
                            className="text-primary underline-offset-4 hover:underline"
                        >
                            {SUPPORT_EMAIL}
                        </a>
                        {' '}
                        and we will help.
                    </p>
                </section>

                <footer className="mt-14 border-t border-border/70 pt-6 text-xs text-muted-foreground">
                    See our
                    {' '}
                    <a
                        href="/tipa/privacy-policy"
                        className="text-primary underline-offset-4 hover:underline"
                    >
                        Privacy Policy
                    </a>
                    {' '}
                    for more on how TIPA handles your data.
                </footer>
            </main>
        </div>
    );
}

export default DeleteAccount;
